import React from 'react'
import PropTypes from 'prop-types'
import invalid from '../../../assets/images/invalid.svg'

/**
 * InputFile component renders a file input field with optional error handling.
 * It supports displaying validation errors and allows for customization via props.
 *
 * @module form/InputFile
 * @description InputFile component renders a file input field with optional error handling.
 * It supports displaying validation errors and allows for customization via props.
 * @param {Object} field - Configuration for the file input field, including name, placeholder, and size.
 * @param {Object} errors - Validation errors object, typically provided by react-hook-form.
 * @param {Function} register - Function to register the input field, typically provided by react-hook-form.
 *
 * @example
 * const field = {
 *   name: 'upload',
 *   placeholder: 'Upload your file',
 *   size: 6,
 *   hidden: false,
 * };
 * const errors = {};
 * const register = () => {};
 *
 * <InputFile field={field} errors={errors} register={register} />
 *
 * @returns {React.Element} The rendered InputFile component.
 */
export const InputFile = ({ field, errors, register }) => {
  try {
    return (
      <div
        className={`trtui-col-md-${field.size} trtui-has-validation`}
        style={{ display: field.hidden ? 'none' : 'block' }}
      >
        <label className="trtui-form-control-label">{field.placeholder}</label>
        <div className="trtui-input-group trtui-has-validation">
          <input
            type="file"
            className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
            style={{
              color: '#000000',
              paddingRight: 'calc(1.5em + 1rem)',
              backgroundImage: errors[field.name] && `url("${invalid}")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right calc(0.375em + 0.25rem) center',
              backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
            }}
            {...register(field.name)}
          />
          {errors[field.name] && (
            <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
          )}
        </div>
      </div>
    )
  } catch (err) {
    return <div>Error</div>
  }
}

InputFile.propTypes = {
  /**
   * Configuration object for the file input field.
   */
  field: PropTypes.object.isRequired,

  /**
   * Validation errors object, typically provided by react-hook-form.
   */
  errors: PropTypes.object.isRequired,

  /**
   * Function to register the input field, typically provided by react-hook-form.
   */
  register: PropTypes.func.isRequired,
}
